import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import path from 'path';

import { Edit as EditIcon } from '@mui/icons-material';
import { Box, Link as MuiLink, Portal, Stack } from '@mui/material';
import {
	GridColDef,
	GridFilterModel,
	GridInitialState,
	GridPaginationModel,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link as ReactRouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { DataGrid, DataGridToolbar, GridActionsCellItem } from '~components';
import { pagePathSegments } from '~constants';
import { NfcTag, NfcTagTypeEnum, SkcKeysService } from '~features/nfc';
import { MutationRefProps } from '~interfaces/refProps';

const toolbarIdString = 'topology-access-rules-datagrid';

const service = new SkcKeysService();

interface SkcNfcTagsDataGridProps {
	onEdit?: (value: NfcTag) => void;
}

const SkcNfcTagsDataGrid = forwardRef<MutationRefProps, SkcNfcTagsDataGridProps>(
	({ onEdit }, ref) => {
		const { t } = useTranslation();

		const columns = useMemo<GridColDef<NfcTag>[]>(
			() => [
				{ field: 'id', headerName: 'ID', width: 80, hideable: false },
				{
					field: 'label',
					headerName: t('name'),
					flex: 1,
				},
				{
					field: 'tagNumber',
					headerName: t('tagNumber'),
					valueFormatter: (value?: string) => value ?? '-',
					flex: 1,
				},
				{
					field: 'rfidUid',
					headerName: t('rfidUid'),
					// valueFormatter: (value: NfcTagTypeEnum) =>
					// 	value === NfcTagTypeEnum.Skopei ? 'Skopei' : t('ui.label.external'),
					flex: 1,
				},
				{
					field: 'tagType',
					headerName: t('views.usersAndOrganisations.nfcCards.tagType'),
					valueFormatter: (value: NfcTagTypeEnum) =>
						value === NfcTagTypeEnum.Skopei ? 'Skopei' : t('ui.label.external'),
					flex: 1,
				},
				{
					field: 'organisation',
					headerName: t('organisation'),
					// valueFormatter: (value: BaseReference) => value?.label ?? '-',
					renderCell: (params) =>
						params.value != null ?
							<MuiLink
								component={ReactRouterLink}
								to={`/${path.join(pagePathSegments.Organisations, params.value.id.toString(), pagePathSegments.Summary)}`}
							>
								{params.value.label}
							</MuiLink>
						:	'-',
					flex: 1,
				},
				{
					field: 'actions',
					type: 'actions',
					display: 'text',
					width: 50,
					resizable: false,
					align: 'right',
					hideable: false,
					getActions: ({ id, ...params }) => [
						<GridActionsCellItem
							key={`${id}-edit`}
							label={t('ui.edit')}
							icon={<EditIcon />}
							color='inherit'
							onClick={() => onEdit?.(params.row)}
							showInMenu
						/>,
					],
				},
			],
			[t, onEdit]
		);

		const [fetchParameters, setFetchParameters] = useState<object>({
			includeUsers: false,
		});
		const { data, isLoading, isValidating, error, mutate } = useSWR(
			// Wait for the pagesize to be set, otherwise we do a double fetch on mount
			fetchParameters.pageSize != null ? [service.basePath, fetchParameters] : null,
			([_, args]) => {
				if (isLoading) {
					service.abortCurrentRequest('Parameters change');
				}

				return service.getKeys(args);
			},
			{
				keepPreviousData: true,
			}
		);

		const [totalCount, setTotalCount] = useState<number>(data?.total || 0);

		useEffect(() => {
			if (data?.total != null) {
				setTotalCount(data.total);
			}
		}, [data]);

		useImperativeHandle(
			ref,
			() => ({
				mutate: mutate,
			}),
			[mutate]
		);

		const handleStateRestore = (state: GridInitialState) => {
			const searchQuery = state.filter?.filterModel?.quickFilterValues?.[0];
			const organisation = state.filter?.filterModel?.items.find(
				(el) => el.field === 'organisation'
			)?.value;

			setFetchParameters((prev) => ({
				...prev,
				page:
					state.pagination?.paginationModel?.page != null ?
						state.pagination.paginationModel.page + 1
					:	undefined,
				pageSize: state.pagination?.paginationModel?.pageSize,
				organisationId: organisation?.id,
				searchQuery: searchQuery,
			}));
		};

		const handlePaginationChange = (value: GridPaginationModel) => {
			setFetchParameters((prev) => ({
				...prev,
				page: value.page + 1,
				pageSize: value.pageSize,
			}));
		};

		const handleFilterModelChange = (model: GridFilterModel) => {
			const searchQuery = model.quickFilterValues?.[0];
			const organisation = model.items.find((el) => el.field === 'organisation')?.value;

			setFetchParameters((prev) => ({
				...prev,
				organisationId: organisation?.id,
				searchQuery: searchQuery,
			}));
		};

		return (
			<Stack
				spacing={2}
				sx={{
					display: 'flex',
					height: 1,
				}}
			>
				<Box id={toolbarIdString} />
				<DataGrid
					keepNonExistentRowsSelected
					disableColumnSorting
					snapshotKey='accnfc'
					columns={columns}
					error={error}
					loading={isLoading || isValidating}
					rows={data?.results ?? []}
					rowCount={totalCount}
					onPaginationModelChange={handlePaginationChange}
					onFilterModelChange={handleFilterModelChange}
					onStateRestore={handleStateRestore}
					slots={{
						toolbar: Toolbar,
					}}
					slotProps={{
						noRowsOverlay: {
							title: t('noResourceFound', {
								resource: t('nfcTags'),
								formatParams: { ignore: true },
							}),
						},
						noResultsOverlay: {
							title: t('noResourceFound', {
								resource: t('nfcTags'),
								formatParams: { ignore: true },
							}),
						},
					}}
					sx={{ flexGrow: 1, flexBasis: 200 }}
				/>
			</Stack>
		);
	}
);

const Toolbar = () => (
	<Portal container={() => document.getElementById(toolbarIdString)}>
		<DataGridToolbar organisationSyncMode='url' />
	</Portal>
);

export default SkcNfcTagsDataGrid;
